import { clearPostAuthAnimationFlag } from "src/utils/auth";
import { isMedProductName } from "src/utils/plans";
import { routeConstants } from "src/utils/constants";
import {
  noomMedOfferLayer,
  paymentCheckoutLayer,
} from "src/pageDefinitions/goto/constants";
import goto from "src/pageDefinitions/goto";
import { trackEvent } from "src/utils/api/tracker";
import { getActiveProgram } from "src/utils/api/userDetails";
import { captureException } from "src/utils/error";
import {
  getSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import getStore from "src/utils/redux/store";
import { loadActiveSubscription } from "src/utils/redux/slices/billingData";
import { isRequestSuccess } from "src/utils/redux/requestState";

declare module "src/pageDefinitions/session" {
  interface BrowserSessionState {
    isPostAuthCheckoutSession?: boolean;
  }
}

export function isPostAuthCheckoutSession(): boolean {
  return getSessionState("browser").isPostAuthCheckoutSession === true;
}

export type UserType = "noomWeight" | "noomMed" | "freemium" | "noSubscription";

/** Must match {@link startMeasuring} in post-auth flow (see PostAuthAnimation). */
export const POST_AUTH_ACCOUNT_CHECK_TASK_NAME = "postAuthAccountCheck";

const productSwitchLayer = "product-switch";

async function gotoAfterPostAuth(userType: UserType) {
  updateSessionState("browser", {
    isPostAuthCheckoutSession: true,
  });
  switch (userType) {
    case "freemium":
      await goto.layer(
        paymentCheckoutLayer,
        null,
        true,
        routeConstants.freemiumClinical,
        undefined
      );
      break;
    case "noSubscription":
      await goto.layer(
        paymentCheckoutLayer,
        null,
        true,
        routeConstants.clinical,
        undefined
      );
      break;
    case "noomWeight": {
      await goto.layer(
        noomMedOfferLayer,
        null,
        true,
        undefined,
        "medUpgradePayment"
      );
      break;
    }
    case "noomMed":
      await goto.layer(productSwitchLayer, null, true);
      break;
    default:
      break;
  }
}

export async function getUserType(accessCode?: string): Promise<UserType> {
  if (!accessCode) {
    return "noSubscription";
  }

  const activeProgram = await getActiveProgram(accessCode);
  if (!activeProgram) {
    return "noSubscription";
  }

  const slug = activeProgram.programConfigurationSlug ?? "";
  if (slug.startsWith("FREE")) {
    return "freemium";
  }

  // Reuses the same billingData.activeSubscription source of truth as the
  // med-upgrade flow's own native-purchase eligibility check.
  await getStore().dispatch(loadActiveSubscription());
  const { activeSubscription } = getStore().getState().billingData;
  if (!isRequestSuccess(activeSubscription) || !activeSubscription.data) {
    return "noSubscription";
  }

  if (isMedProductName(activeSubscription.data.productName)) {
    return "noomMed";
  }

  return "noomWeight";
}

export async function navigateAfterAnimation(
  userType: UserType,
  latencyMs: number | undefined
): Promise<void> {
  clearPostAuthAnimationFlag();

  trackEvent("PostAuthAccountCheckCompleted", {
    userType,
    latencyMs,
  });

  if (userType === "noomWeight") {
    trackEvent("PostAuthUpgradeEligibilityDetermined", {
      userType,
      eligible: true,
    });

    trackEvent("PostAuthUpgradeFlowInitiated", {
      userType,
    });
  } else if (userType === "noomMed") {
    trackEvent("PostAuthRoutedToProductSwitch", {
      userType,
    });
  } else {
    trackEvent("PostAuthRoutedToCheckout", {
      userType,
    });
  }

  try {
    await gotoAfterPostAuth(userType);
  } catch (err) {
    captureException(err, "PostAuthGotoFailed", { userType });
  }
}
